import { useQuery } from '@tanstack/react-query'
import { Auth } from 'aws-amplify'
import { requestForServer } from '../Utils/ServerUtil'
import { GET_ORGANIZATIONS } from '../Constants'

/**
 * Custom hook to fetch organizations using React Query
 * 
 * @returns {Object} Object containing organizations data, loading state, and error state
 */
export const useFetchOrganizations = () => {
  return useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      try {
        await Auth.currentSession()
      } catch (e) {
        // Session is gone (logout in progress) — return empty silently.
        return []
      }

      const result = await requestForServer(GET_ORGANIZATIONS)

      if (result.error) {
        // Primary check: HTTP 401/403 means auth is gone — suppress silently.
        // result.error.status is now set by requestForServer.
        const status = result.error?.status
        if (status === 401 || status === 403) return []

        // Belt-and-suspenders: if the session disappeared during the request
        // (Auth.signOut() resolved mid-flight), also suppress silently.
        try {
          await Auth.currentSession()
        } catch (sessionErr) {
          return []
        }

        throw (result.error instanceof Error)
          ? result.error
          : new Error(String(result.error))
      }

      return result.data || []
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 0, // Don't retry on logout — the first failure is definitive.
  })
}